import {
  AdEnginePlugin,
  type AuctionData,
  type Hooks,
} from "../../core/AdEnginePlugin";
import { PrebidWrapper } from "./PrebidWrapper";
import prebidDefaultConfig from "../../config/prebid";
import { bidderAdapters, type PrebidAdapterConfig } from "./Adapters/Adapter";
import isPrebidDebugEnabled from "./isPrebidDebugEnabled";
import { waitWithTimeout } from "utils";
import invariant from "tiny-invariant";
import type { PrebidBidderAdapter } from "./Adapters/PrebidBidderAdapter";
import type { AdEngine } from "../../core/AdEngine";
import type { TieredParams } from "../../core/MonetizationTierEngine";
import type { InstantConfigRepository } from "instant-config";
import {
  extendPrebidConfigWithIdPartners,
  type PrebidIdPartnerConfig,
  registerAsyncPrebidIdPartners,
} from "./IdPartners/IdPartners";

type OnReadyCallback = (pbjs: Prebid) => void;

export interface PrebidPluginAPI {
  addPrebidReadyCallback: (callback: OnReadyCallback) => void;
}

export type PrebidPluginConfig = PrebidAdapterConfig &
  PrebidIdPartnerConfig &
  Pick<
    InstantConfigRepository,
    "icAdEngineAuctionTimeoutMS" | "icPrebidInitTimeoutMS"
  >;

/**
 * PrebidPlugin is responsible for setting up Prebid.js and requesting bids.
 * It uses the `onCreateBiddersRequests` hook to request bids
 */
export class PrebidPlugin extends AdEnginePlugin<PrebidPluginAPI> {
  private adEngine?: AdEngine;
  private readonly prebidWrapper: PrebidWrapper;
  /**
   * Map of active bidders that are enabled in the current configuration to their
   * respective {@link PrebidBidderAdapter} instances. These are used to transform
   * the ad unit bids to only include bidders that are enabled, and allow the bidders
   * to transform the ad unit bids before they are sent to Prebid.js if needed.
   */
  private readonly activeBidders: Map<
    PrebidBidderName,
    PrebidBidderAdapter<PrebidBidderName>
  > = new Map();

  constructor(private readonly config: PrebidPluginConfig) {
    super();

    this.prebidWrapper = this.initPrebidWrapper();
    this.initBidderAdapters();
  }

  /**
   * Waits for Prebid.js to be ready by adding a callback to the
   * `pbjs.addPrebidReadyCallback` function. This will resolve when the callback
   * is called, indicating that Prebid.js is ready to use.
   *
   * If Prebid.js is not ready within the
   * {@link InstantConfigRepository.icPrebidInitTimeoutMS}, it will throw an error.
   *
   * @returns A promise that resolves when Prebid.js is ready.
   * @throws Will throw an error if Prebid.js is not ready within the timeout.
   */
  private async waitForPrebidReady(): Promise<void> {
    const pbjsReadyPromise = new Promise<void>((resolve) => {
      this.api.addPrebidReadyCallback(() => resolve());
    });

    await waitWithTimeout(
      pbjsReadyPromise,
      this.config.icPrebidInitTimeoutMS,
      "PrebidPlugin.waitForPrebidReady",
    );
  }

  private initPrebidWrapper(): PrebidWrapper {
    const prebidConfig = extendPrebidConfigWithIdPartners(
      {
        ...prebidDefaultConfig,
        bidderTimeout: this.config.icAdEngineAuctionTimeoutMS,
        debug: isPrebidDebugEnabled(),
      },
      this.config,
    );

    const wrapper = new PrebidWrapper(prebidConfig);

    wrapper.pushToQueue((pbjs) =>
      registerAsyncPrebidIdPartners(this.config, this, pbjs),
    );

    return wrapper;
  }

  /**
   * Initializes the bidder adapters, adding any enabled adapters to the
   * `activeBidders` map on this instance.
   *
   * Any inactive bidders will be dropped, when generating the AdUnits at
   * auction time.
   */
  private initBidderAdapters() {
    for (const [bidder, bidderAdapter] of Object.entries(bidderAdapters)) {
      if (bidderAdapter.isEnabled(this.config)) {
        this.activeBidders.set(bidder as PrebidBidderName, bidderAdapter);
      }
    }
  }

  /**
   * Takes the provided {@link PrebidBidderSlotConfig} and transforms it into an
   * array of {@link PrebidAdUnitBid} objects that will be added to the ad unit.
   *
   * This will loop through each `activeBidders` and drop any bidders that are
   * not active, as well as allowing the active bidders to transform the bid
   * configurations if required.
   *
   * The final array of bids will be returned to be added to the ad unit.
   *
   * @param bidderSlotConfig The prebid configuration for the current ad node.
   * @param auctionData The auction data for the current auction.
   * @returns The list of PrebidAdUnitBid objects to add to `adUnit.bids` parameter
   */
  private generateAdUnitBids(
    bidderSlotConfig: PrebidBidderSlotConfig,
    auctionData: AuctionData,
  ): PrebidAdUnitBid[] {
    const adUnitBids: PrebidAdUnitBid[] = [];
    for (const [bidder, bidderConfigs] of Object.entries(
      bidderSlotConfig.bids,
    ) as [PrebidBidderName, PrebidAdUnitBid[]][]) {
      if (this.activeBidders.has(bidder)) {
        let transformedBids =
          this.activeBidders
            .get(bidder)
            ?.transformAdUnitBidConfigs(
              bidderConfigs,
              auctionData,
              this.config,
            ) ?? [];

        transformedBids = this.applyMonetizationTiersToBids(transformedBids);

        adUnitBids.push(...transformedBids);
      }
    }
    return adUnitBids;
  }

  /**
   * Apply tiered monetization treatment to the bids.
   * This will filter the params based on the current monetization tier,
   * ensuring that the bids are only sent with the parameters that are valid
   * for the current tier.
   * Additionally, if bidder does not define current tier in Odyssey's config,
   * the bidder will be DISABLED.
   *
   * @param adUnitBids The list of PrebidAdUnitBid objects to apply the monetization tiers to.
   * @returns The list of PrebidAdUnitBid objects with the params filtered by the
   * current monetization tier.
   * @see {@link MonetizationTierEngine.filterTieredParamsByCurrentTier}
   */
  applyMonetizationTiersToBids(
    adUnitBids: PrebidAdUnitBid[],
  ): PrebidAdUnitBid[] {
    const monetizationEngine = this.adEngine?.monetizationTierEngine;

    if (!monetizationEngine) {
      console.error(
        "PrebidPlugin.applyMonetizationTiersToBids: MonetizationTierEngine is not available.",
      );
      return adUnitBids;
    }

    // We will filter the params based on the current monetization tier.
    const adUnitBidsInCurrentTier = [];
    for (const adUnitBid of adUnitBids) {
      // If the bid does not have params, we will skip it.
      if (!adUnitBid.params) {
        continue;
      }

      // If the bid has params, we will filter them by the current monetization tier.
      const params = monetizationEngine.filterTieredParamsByCurrentTier(
        adUnitBid.params as TieredParams<AllBiddersBidParams>,
      );

      // If the filtered params are undefined, we will skip the bid.
      if (typeof params === "undefined") {
        continue;
      }

      // If the params are not empty, we will add the bid to the filtered list.
      adUnitBidsInCurrentTier.push({
        ...adUnitBid,
        params,
      } satisfies PrebidAdUnitBid);
    }

    return adUnitBidsInCurrentTier;
  }

  /**
   * This generates the {@link PrebidAdUnit} objects for each AdNode provided in
   * the auction data object. The AdUnits will then be passed directly to the
   * `pbjs.requestBids` function which uses the provided ad units to request
   * bids from the configured bidders.
   *
   * These are generated for every auction, ensuring that:
   * - The ad units are always up to dat with the latest ad node and size configuration
   * - page targeting is always up to date
   * - prebid _only_ bids on the ad nodes requested in the current auction
   *
   * @param auctionData The auction with the AdNodes and targeting to create the Prebid ad units
   * @returns The list of all Prebid ad units to be used in the auction.
   */
  private generateAdUnits(auctionData: AuctionData): PrebidAdUnit[] {
    const adUnits: PrebidAdUnit[] = [];
    for (const adNode of auctionData.adNodes) {
      // Get the prebid bidder slot config for the ad node, skipping if
      // that ad node is not configured for prebid.
      const prebidBidderSlotConfig = adNode.getBidderConfig("prebid");
      if (!prebidBidderSlotConfig) continue;

      // Convert the current BidderSlotConfigMap to the actual array of
      // PrebidAdUnitBid objects, allowing the bidder adapters to transform
      // the bids, or the plugin to drop bidders that are not enabled.
      let adUnitBids: PrebidAdUnitBid[] = [];
      if (prebidBidderSlotConfig.bids) {
        adUnitBids = this.generateAdUnitBids(
          prebidBidderSlotConfig,
          auctionData,
        );
      }

      // Create the ad unit object
      const adUnit: PrebidAdUnit = {
        ...prebidBidderSlotConfig,
        code: adNode.getSlotName(),
        bids: adUnitBids,
      } satisfies PrebidAdUnit;

      adUnits.push(adUnit);
    }

    return adUnits;
  }

  /**
   * Runs a prebid auction with the provided auction data. Resolves when the
   * auction is complete.
   *
   * @param auctionData Auction data to run the auction including what adNodes to bid for.
   * @param onCreateBidderAdUnit callback to emit the hook when adUnits are created.
   * @returns Promise that resolves when the auction is complete.
   */
  private async requestBids(
    auctionData: AuctionData,
    onCreateBidderAdUnit: NonNullable<Hooks["onCreateBidderAdUnit"]>,
  ) {
    invariant(
      this.prebidWrapper.isPrebidReady(),
      "PrebidPlugin.requestBids: Prebid.js is not ready",
    );

    return new Promise<void>((resolve) => {
      const adUnits = this.generateAdUnits(auctionData);

      // Allow plugins to modify the ad units before they are sent to Prebid.js
      onCreateBidderAdUnit("prebid", auctionData, adUnits);

      this.prebidWrapper.requestBids({
        auctionId: auctionData.auctionId,
        adUnits,
        bidsBackHandler: () => {
          this.prebidWrapper.setTargetingForGPTAsync();
          resolve();
        },
      });
    });
  }

  hooks: Hooks = {
    onBiddersInit: (adEngine, initPromises) => {
      this.adEngine = adEngine;
      // Verify prebid is ready before proceeding. We will wait for a small timeout
      // to allow prebid to initialize. If it doesn't initialize in time, we will
      // throw an error.
      initPromises.push(this.waitForPrebidReady());
    },
    onCreateBiddersRequests: (
      bidderRequests: Promise<void>[],
      auctionData: AuctionData,
      adEngine: AdEngine,
      onCreateBidderAdUnit: NonNullable<Hooks["onCreateBidderAdUnit"]>,
    ) => {
      bidderRequests.push(this.requestBids(auctionData, onCreateBidderAdUnit));
    },
  };

  api = {
    addPrebidReadyCallback: (callback: OnReadyCallback) => {
      this.prebidWrapper.pushToQueue(callback);
    },
  };
}
